import { isNumber, upperCamelCase } from '@art-suite/art-core-ts';
import { getEffectiveScanValueAsNumber } from '../ScanRoot';
import { ScanGroupOptimized, ScanLineItemOptimized, ScanOptionOptimized, ScanPartOptimized, ScanTaskOptimized } from '../ScanRootOptimized';
import { ScanTemplateGroup, ScanTemplateOption, ScanTemplateRoot, ScanTemplateTask } from '../ScanTemplateRoot';

export type ClusteredOrderedTasks = {
  clusterOrder: string[];
  taskClusters: Record<string, ScanTaskOptimized[]>;
  taskIdClusters: Record<string, string[]>;
};

export type ClusteredOrderedGroups = {
  clusterOrder: string[];
  groupClusters: Record<string, ScanGroupOptimized[]>;
};

export const getNormalizedCategory = (task: ScanPartOptimized): string => {
  return upperCamelCase(task.template.category || 'other', ' ');
};

const filterScanParts = <T extends ScanPartOptimized>(scanParts: T[] | undefined, filter?: RegExp | ((item: T) => boolean)): T[] => {
  if (!scanParts) return [];
  if (!filter) return scanParts;
  if (filter instanceof RegExp) {
    return scanParts.filter(item => filter.test(item.optimized.searchText));
  }
  return scanParts.filter(filter as (item: T) => boolean);
};

export const getTasksInStandardSortOrder = (
  groupTemplate?: ScanTemplateGroup,
  groupTasks?: ScanTaskOptimized[],
  filter?: RegExp | ((task: ScanTaskOptimized) => boolean),
): ClusteredOrderedTasks => {
  if (!groupTemplate || !groupTasks) return { clusterOrder: [], taskClusters: {}, taskIdClusters: {} };
  const filteredTasks = filterScanParts(groupTasks, filter);
  const groupTemplateIdToSortIndex: Record<string, number> = {};
  groupTemplate.taskTemplateIds?.forEach((taskTemplateId, index) => {
    groupTemplateIdToSortIndex[taskTemplateId] = index;
  });

  // tasks should be in the order of the group template (and if two tasks have the same template, they should be in the order of the groupsTasks)
  const tasks = filteredTasks.sort((a, b) => groupTemplateIdToSortIndex[a.template.id] - groupTemplateIdToSortIndex[b.template.id]);

  // then we cluster the tasks by category
  const taskClusters: Record<string, ScanTaskOptimized[]> = {};
  const taskIdClusters: Record<string, string[]> = {};
  tasks.forEach(task => {
    const category = getNormalizedCategory(task);
    if (!taskClusters[category]) taskClusters[category] = [];
    taskClusters[category].push(task);
    if (!taskIdClusters[category]) taskIdClusters[category] = [];
    taskIdClusters[category].push(task.id);
  });
  return {
    clusterOrder: Object.keys(taskClusters).sort(),
    taskClusters,
    taskIdClusters,
  };
};

export const getGroupsInStandardGroupOrder = (
  rootTemplate?: ScanTemplateRoot,
  groups?: ScanGroupOptimized[],
  filter?: RegExp | ((group: ScanGroupOptimized) => boolean),
): ClusteredOrderedGroups => {
  if (!rootTemplate || !groups) return { clusterOrder: [], groupClusters: {} };
  const filteredGroups = filterScanParts(groups, filter);
  const groupTemplateIdToSortIndex: Record<string, number> = {};
  rootTemplate.groupTemplates?.forEach((groupTemplate, index) => {
    groupTemplateIdToSortIndex[groupTemplate.id] = index;
  });

  const orderedGroups = filteredGroups.sort((a, b) => groupTemplateIdToSortIndex[a.template.id] - groupTemplateIdToSortIndex[b.template.id]);

  const groupClusters: Record<string, ScanGroupOptimized[]> = {};
  orderedGroups.forEach(group => {
    const category = getNormalizedCategory(group);
    if (!groupClusters[category]) groupClusters[category] = [];
    groupClusters[category].push(group);
  });
  return {
    clusterOrder: Object.keys(groupClusters).sort(),
    groupClusters,
  };
};

// this is simpler - there are no categories for options
export const getOptionsInStandardSortOrder = (
  taskTemplate: ScanTemplateTask,
  taskOptions: ScanOptionOptimized[] | undefined,
  filter?: RegExp | ((option: ScanOptionOptimized) => boolean),
): ScanOptionOptimized[] => {
  const filteredOptions = filterScanParts(taskOptions, filter);
  const optionTemplateIdToSortIndex: Record<string, number> = {};
  taskTemplate.optionTemplates?.forEach((optionTemplate, index) => {
    optionTemplateIdToSortIndex[optionTemplate.id] = index;
  });

  return filteredOptions.sort((a, b) => optionTemplateIdToSortIndex[a.template.id] - optionTemplateIdToSortIndex[b.template.id]);
};

export const getLineItemsInStandardSortOrder = (
  optionTemplate: ScanTemplateOption,
  optionLineItems: ScanLineItemOptimized[],
  filter?: RegExp | ((lineItem: ScanLineItemOptimized) => boolean),
): ScanLineItemOptimized[] => {
  const filteredLineItems = filterScanParts(optionLineItems, filter);
  const lineItemTemplateIdToSortIndex: Record<string, number> = {};
  optionTemplate.lineItemTemplates?.forEach((lineItemTemplate, index) => {
    lineItemTemplateIdToSortIndex[lineItemTemplate.id] = index;
  });

  return filteredLineItems.sort((a, b) => lineItemTemplateIdToSortIndex[a.template.id] - lineItemTemplateIdToSortIndex[b.template.id]);
};

export type GroupMeasurementNumbers = {
  area: number | undefined;
  perimeter: number | undefined;
  wallArea: number | undefined;
};

export const getGroupMeasurements = (group?: ScanGroupOptimized): GroupMeasurementNumbers => {
  if (!group) return { area: undefined, perimeter: undefined, wallArea: undefined };
  const reducedArea = group.optimized?.reducedValues?.roomArea;
  return {
    area: typeof reducedArea === 'number'
      ? reducedArea
      : getEffectiveScanValueAsNumber(group.values?.roomArea),
    perimeter: getEffectiveScanValueAsNumber(group.values?.roomPerimeter),
    wallArea: getEffectiveScanValueAsNumber(group.values?.roomWallArea),
  };
};

export const getGroupMeasurementTotals = (groups: ScanGroupOptimized[]): GroupMeasurementNumbers => {
  const totals: GroupMeasurementNumbers = { area: undefined, perimeter: undefined, wallArea: undefined };
  for (const group of groups) {
    const values = getGroupMeasurements(group);
    if (isNumber(values.area)) totals.area = (totals.area ?? 0) + values.area;
    if (isNumber(values.perimeter)) totals.perimeter = (totals.perimeter ?? 0) + values.perimeter;
    if (isNumber(values.wallArea)) totals.wallArea = (totals.wallArea ?? 0) + values.wallArea;
  }
  return totals;
};
